import { useRef, ReactNode } from 'react';

import { HorizontalMenuTabs, HorizontalMenuTabsProps, PageHeader } from '@backrs_ui/common-ui';

import { Box, Theme, SxProps } from '@mui/material';

import { ResourcePageSection } from './types';
import { useScrollTabSync } from './useScrollTabSync';

type ResourceWrapperProps = Readonly<{
  header: ReactNode;
  sections: ResourcePageSection[];
  chipLabel?: string;
  showTabs?: boolean;
}>;

export const ResourceWrapper = ({
  header,
  sections,
  chipLabel,
  showTabs = true,
}: ResourceWrapperProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const TAB_TITLES = sections
    .filter((section) => !section.hideFromMenu)
    .map((section) => section.title);
  const TAB_KEYS = sections.filter((section) => !section.hideFromMenu).map((section) => section.id);

  const { activeTabIndex, handleTabChange } = useScrollTabSync({
    scrollContainerRef: contentRef,
    sectionIds: TAB_KEYS,
    useNegativeIndexForTop: true,
  });

  const chipLabelConfig: HorizontalMenuTabsProps['chipConfig'] = chipLabel
    ? {
        label: chipLabel,
        iconName: 'ArrowUp',
        iconColor: 'Colors/Foreground/fg-quaternary',
        onClick: () => handleTabChange(-1),
      }
    : undefined;

  return (
    <Box sx={getContainerSx}>
      <Box sx={mainContentSx} ref={contentRef}>
        {header}

        {showTabs && sections.length > 1 && (
          <HorizontalMenuTabs
            id="resource-wrapper-tabs"
            setValue={handleTabChange}
            tabs={TAB_TITLES}
            value={activeTabIndex}
            chipConfig={chipLabelConfig}
            variant="minimal"
            containerSxOverride={tabsContainerSx}
          />
        )}

        {sections
          .filter((section) => section.visible !== false)
          .map((section) => (
            <Box key={section.id} id={section.id} sx={sectionWrapperSx}>
              {!section.hideHeader && (
                <PageHeader
                  title={section.title}
                  subtitle={section.description}
                  rightComponents={section.headerRightComponents}
                  maxWidth={null}
                  isSubheader
                />
              )}
              <Box sx={sectionContentSx(section.hideHeader)}>
                <Box>{section.component}</Box>
              </Box>
            </Box>
          ))}
        <Box sx={bottomSpacerSx} />
      </Box>
    </Box>
  );
};

const getContainerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
  display: {
    xs: 'block',
    md: 'flex',
  },
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  minHeight: '100%',
  height: '100%',
};

const tabsContainerSx: SxProps<Theme> = {
  maxWidth: '936px !important',
  mx: '0 !important',
};

const mainContentSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: backrs.width['width-2xl'],
  marginX: 'auto',
});

const sectionWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-6xl'],
});

const bottomSpacerSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  height: backrs.spacing['spacing-xxs'],
});

const sectionContentSx =
  (hideHeader?: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    containerType: 'inline-size',
    width: '100%',
    ...(!hideHeader && {
      pt: backrs.spacing['spacing-2xl'],
    }),
    '@container (min-width: 600px)': {
      ...(!hideHeader && {
        pt: backrs.spacing['spacing-4xl'],
      }),
    },
  });
